import { z } from 'zod';

const FISCAL_MONTH = z.number().int().min(1).max(12);
const CURRENCY = z.string().regex(/^[A-Z]{3}$/, 'must be a 3-letter ISO 4217 code');
const addr = (max: number) => z.string().max(max).optional();

export const createCompanyInputSchema = z.object({
  name: z.string().min(1).max(200),
  legalName: z.string().max(200).optional(),
  taxId: z.string().max(50).optional(),
  fiscalYearStartMonth: FISCAL_MONTH.default(1),
  accountingBasis: z.enum(['accrual', 'cash']).default('accrual'),
  currencyCode: CURRENCY.default('USD'),
  addressLine1: addr(200),
  addressLine2: addr(200),
  city: addr(100),
  region: addr(100),
  postalCode: addr(20),
  phone: addr(50),
  notes: z.string().max(2000).optional(),
  includeInConsolidated: z.boolean().optional(),
  cloneCoaTemplate: z.boolean().default(true),
});
export type CreateCompanyInput = z.infer<typeof createCompanyInputSchema>;

const ISO_DATE_OR_NULL = z
  .union([z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD'), z.null()]);

export const updateCompanyInputSchema = createCompanyInputSchema
  .partial()
  .omit({ cloneCoaTemplate: true })
  .extend({
    isActive: z.boolean().optional(),
    periodLockedThrough: ISO_DATE_OR_NULL.optional(),
    /** Expense account for processor fees withheld from a deposit. Null clears it. */
    processorFeeAccountId: z.string().uuid().nullable().optional(),
  });
export type UpdateCompanyInput = z.infer<typeof updateCompanyInputSchema>;

export const companySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  legalName: z.string().nullable(),
  taxId: z.string().nullable(),
  fiscalYearStartMonth: z.number(),
  accountingBasis: z.string(),
  currencyCode: z.string(),
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  city: z.string().nullable(),
  region: z.string().nullable(),
  postalCode: z.string().nullable(),
  phone: z.string().nullable(),
  isActive: z.boolean(),
  notes: z.string().nullable(),
  periodLockedThrough: z.string().nullable(),
  includeInConsolidated: z.boolean(),
  processorFeeAccountId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Company = z.infer<typeof companySchema>;
